<script setup lang="ts">
  import { computed } from "vue";
  import moment from "moment";
  import { Ellipsis, ExternalLink, Pencil } from "lucide-vue-next";
  import { IndexTableDetail } from "@/components/episto-ui/index-table";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
  import SurveyStatusBadge from "@/components/Survey/SurveyStatusBadge.vue";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import { creditsLabel } from "@/utils/credits/creditRules";
  import type { Contract, ContractSurvey } from "@/types";

  const props = withDefaults(defineProps<{ contract: Contract; canManage?: boolean }>(), { canManage: false });

  const emit = defineEmits<{ "set-manual-credits": [payload: { survey: ContractSurvey; contract: Contract }] }>();

  const { openRow } = useRowNavigation();
  const surveys = computed(() => props.contract.surveys ?? []);
  const formatDay = (date: string | null) => (date ? moment(date).format("MMM D, YYYY") : "");
</script>

<template>
  <IndexTableDetail
    bleed
    :title="`Surveys · ${surveys.length}`">
    <p
      v-if="contract.comment"
      class="px-8 pb-2 whitespace-pre-line text-xs italic text-muted-foreground">
      {{ contract.comment }}
    </p>
    <p
      v-if="surveys.length === 0"
      class="px-8 pb-3 text-13 text-muted-foreground">
      No surveys associated.
    </p>
    <div
      v-else
      class="border-t border-secondary">
      <div
        v-for="survey in surveys"
        :key="survey.id"
        class="grid grid-cols-[44px_minmax(0,1fr)_110px_minmax(150px,auto)_minmax(0,190px)_60px] items-center border-b border-secondary py-2 text-13 last:border-b-0">
        <span />
        <div class="flex min-w-0 flex-col px-3">
          <div class="flex min-w-0 items-center gap-2">
            <span
              class="truncate text-foreground"
              :title="survey.name">
              {{ survey.name }}
            </span>
            <Badge
              v-if="survey.ai_interview"
              variant="purple"
              class="shrink-0 text-3xs uppercase tracking-[0.06em]">
              Quali
            </Badge>
          </div>
          <span
            class="truncate text-2xs text-muted-foreground"
            :title="survey.account_name">
            {{ survey.account_name }}
          </span>
        </div>
        <div class="px-3">
          <SurveyStatusBadge :status="survey.status" />
        </div>
        <div class="flex items-center gap-2 px-3">
          <span class="whitespace-nowrap">{{ survey.credit_effective }} {{ creditsLabel(survey.credit_effective) }}</span>
          <span
            v-if="survey.is_manual"
            class="whitespace-nowrap text-xs text-muted-foreground">
            (auto: {{ survey.credits_consumed_auto_effective }})
          </span>
          <Badge
            v-if="survey.is_manual"
            variant="warning"
            class="text-3xs">
            manual
          </Badge>
        </div>
        <div class="min-w-0 px-3 text-2xs text-muted-foreground">
          <p v-if="survey.completed_at">Completed {{ formatDay(survey.completed_at) }}</p>
          <p v-if="survey.owner_name">CSM: {{ survey.owner_name }}</p>
        </div>
        <div class="flex justify-end px-3 pr-8">
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button
                variant="ghost"
                size="icon-sm">
                <Ellipsis class="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem @click="openRow(`/surveys/${survey.token}/questions`)">
                <ExternalLink class="mr-2 size-4" />
                Go to survey
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="canManage"
                @click="emit('set-manual-credits', { survey, contract })">
                <Pencil class="mr-2 size-4" />
                Set manual credits
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  </IndexTableDetail>
</template>
